// src/pages/Admin.jsx
import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  addDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  runTransaction,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../components/firebase/firebase";
import { useNavigate } from "react-router-dom";
import Loader from "../components/common/Loader";
import Alert from "../components/common/Alert";
import { createStudentAccount } from "../lib/createStudentAuth";

const BOARDS = [
  { id: "state", name: "State" },
  { id: "cbse", name: "CBSE" },
  { id: "competitive", name: "Competitive" },
];


// ── Reusable row for each curriculum panel ───────────────────────────────────
function PanelRow({ item, isSelected, isEditing, editName, onEditNameChange, onSelect, onEdit, onSave, onCancel, onDelete }) {
  return (
    <div className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 ${
      isSelected ? "bg-indigo-600 text-white shadow-sm" : "hover:bg-gray-50 text-gray-700"
    }`}>
      {isEditing ? (
        <>
          <input
            value={editName}
            onChange={(e) => onEditNameChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
            className="flex-1 border border-indigo-300 px-2 py-1 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 min-w-0"
            autoFocus
          />
          <button onClick={onSave} className="text-green-600 hover:text-green-800 font-bold text-sm shrink-0">✓</button>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-sm shrink-0">✕</button>
        </>
      ) : (
        <>
          {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white shrink-0"></span>}
          <span onClick={onSelect} className="flex-1 text-sm font-medium truncate select-none">{item.name}</span>
          <div className={`flex items-center gap-1 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className={`text-xs px-1.5 py-0.5 rounded transition-colors ${isSelected ? "text-indigo-200 hover:text-white hover:bg-indigo-500" : "text-blue-400 hover:text-blue-600 hover:bg-blue-50"}`}
              title="Edit"
            >✏</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className={`text-xs px-1.5 py-0.5 rounded transition-colors ${isSelected ? "text-indigo-200 hover:text-red-300 hover:bg-indigo-500" : "text-red-400 hover:text-red-600 hover:bg-red-50"}`}
              title="Delete"
            >✕</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Key/Label auto-generators ─────────────────────────────────────────────────
const toKey = (name) => name.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
const toLabel = (name) => { const m = name.match(/\d+/); return m ? m[0] : toKey(name); };

// ────────────────────────────────────────────────────────────────────────────
const Admin = () => {
  // ── General ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  const showMsg = (msg, type) => {
    setAlertMessage(msg);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleLogout = () => { localStorage.clear(); navigate("/login"); };

  // ── Pricing ────────────────────────────────────────────────────────────────
  const [cards, setCards] = useState({});
  const categoryNames = { "10th12th": "10 & 12th CBSE/State", jee: "JEE", neet: "NEET" };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "pricing"));
        let data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const order = [
          "10th12th-mon","10th12th-half","10th12th-ann",
          "jee-mon","jee-half","jee-ann",
          "neet-mon","neet-half","neet-ann",
        ];
        data = data.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
        setCards(
          data.reduce((acc, curr) => {
            const cat = curr.id.split("-")[0];
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(curr);
            return acc;
          }, {})
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleInputChange = (category, index, e) => {
    const newCards = { ...cards };
    newCards[category][index][Object.keys(newCards[category][index])[1]] = e.target.value;
    setCards(newCards);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      for (const cat of Object.keys(cards))
        for (const card of cards[cat])
          await setDoc(doc(db, "pricing", card.id), card, { merge: true });
      showMsg("Pricing updated successfully", "success");
    } catch {
      showMsg("Error updating pricing", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Curriculum Manager ──────────────────────────────────────────────────────
  const [currBoard, setCurrBoard] = useState("state");
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selGrade, setSelGrade] = useState(null);

  // Add-form inputs
  const [newGradeName, setNewGradeName] = useState("");
  const [newSubjectName, setNewSubjectName] = useState("");

  // Inline-edit state
  const [editItem, setEditItem] = useState(null); // { level, id }
  const [editName, setEditName] = useState("");

  // Firestore collection refs
  const gradesColl = (board) => collection(db, "curriculum", board, "grades");
  const subjectsColl = (board, gId) =>
    collection(db, "curriculum", board, "grades", gId, "subjects");

  const sortByOrder = (arr) => [...arr].sort((a, b) => (a.order || 0) - (b.order || 0));

  const fetchGrades = async (board) => {
    const snap = await getDocs(gradesColl(board));
    setGrades(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    setSelGrade(null); setSubjects([]);
  };

  const fetchSubjects = async (board, gId) => {
    const snap = await getDocs(subjectsColl(board, gId));
    setSubjects(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGrades(currBoard); }, [currBoard]);

  // Grade CRUD
  const handleAddGrade = async () => {
    if (!newGradeName.trim()) return;
    await addDoc(gradesColl(currBoard), {
      name: newGradeName.trim(), label: toLabel(newGradeName), order: grades.length + 1,
    });
    setNewGradeName("");
    fetchGrades(currBoard);
  };
  const handleUpdateGrade = async (gId) => {
    await updateDoc(doc(db, "curriculum", currBoard, "grades", gId), {
      name: editName.trim(),
    });
    setEditItem(null);
    fetchGrades(currBoard);
  };
  const handleDeleteGrade = async (gId) => {
    if (!window.confirm("Delete this grade and all its subjects?")) return;
    await deleteDoc(doc(db, "curriculum", currBoard, "grades", gId));
    if (selGrade?.id === gId) { setSelGrade(null); setSubjects([]); }
    fetchGrades(currBoard);
  };

  // Subject CRUD
  const handleAddSubject = async () => {
    if (!selGrade || !newSubjectName.trim()) return;
    await addDoc(subjectsColl(currBoard, selGrade.id), {
      name: newSubjectName.trim(), key: toKey(newSubjectName), order: subjects.length + 1,
    });
    setNewSubjectName("");
    fetchSubjects(currBoard, selGrade.id);
  };
  const handleUpdateSubject = async (sId) => {
    await updateDoc(
      doc(db, "curriculum", currBoard, "grades", selGrade.id, "subjects", sId),
      { name: editName.trim() }
    );
    setEditItem(null);
    fetchSubjects(currBoard, selGrade.id);
  };
  const handleDeleteSubject = async (sId) => {
    if (!window.confirm("Delete this subject?")) return;
    await deleteDoc(
      doc(db, "curriculum", currBoard, "grades", selGrade.id, "subjects", sId)
    );
    fetchSubjects(currBoard, selGrade.id);
  };

  // ── Upload PDF (cascading dropdowns) ───────────────────────────────────────
  const [uploadBoard, setUploadBoard] = useState("");
  const [uploadGradeId, setUploadGradeId] = useState("");
  const [uploadSubjectId, setUploadSubjectId] = useState("");
  const [uploadGrades, setUploadGrades] = useState([]);
  const [uploadSubjects, setUploadSubjects] = useState([]);
  const todayStr = () => new Date().toISOString().split("T")[0];
  const [uploadDate, setUploadDate] = useState(todayStr());
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [referenceLink, setReferenceLink] = useState("");
  const [pdfs, setPdfs] = useState([]);

  // ── PDF filter + multi-select ─────────────────────────────────────────
  const [pdfFilterGrade, setPdfFilterGrade] = useState("");
  const [pdfFilterSubject, setPdfFilterSubject] = useState("");
  const [selectedPdfs, setSelectedPdfs] = useState(new Set());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!uploadBoard) { setUploadGrades([]); setUploadGradeId(""); return; }
    getDocs(gradesColl(uploadBoard)).then((snap) =>
      setUploadGrades(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );
    setUploadGradeId(""); setUploadSubjectId("");
  }, [uploadBoard]); // eslint-disable-line

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!uploadBoard || !uploadGradeId) { setUploadSubjects([]); setUploadSubjectId(""); return; }
    getDocs(subjectsColl(uploadBoard, uploadGradeId)).then((snap) =>
      setUploadSubjects(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );
    setUploadSubjectId("");
  }, [uploadBoard, uploadGradeId]); // eslint-disable-line

  const isUploadDisabled =
    !uploadBoard || !uploadGradeId || !uploadSubjectId || !title || !uploadDate;

  const fetchPDFs = async () => {
    const snap = await getDocs(collection(db, "resources"));
    setPdfs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleFileUpload = async (e) => {
    setLoading(true);
    try {
      const uploadedFile = e.target.files[0];
      if (!uploadedFile) return;

      const gradeObj = uploadGrades.find((g) => g.id === uploadGradeId);
      const subjectObj = uploadSubjects.find((s) => s.id === uploadSubjectId);

      // Derive month name + year from date picker value
      const dateObj = new Date(uploadDate + "T00:00:00");
      const monthName = dateObj.toLocaleString("default", { month: "long" });
      const yearNum = dateObj.getFullYear().toString();

      // Generate reference ID: e.g. STB10MAT-241115-A3F9
      const boardCode = { state: "STB", cbse: "CBS", competitive: "CMP" }[uploadBoard]
        || uploadBoard.slice(0, 3).toUpperCase();
      const gradeCode = (gradeObj?.label || "").toString().padStart(2, "0");
      const subjCode = (subjectObj?.name || subjectObj?.key || "").slice(0, 3).toUpperCase();
      const dateCode = uploadDate.replace(/-/g, "").slice(2); // YYMMDD
      const randHex = Math.floor(Math.random() * 65536).toString(16).toUpperCase().padStart(4, "0");
      const refId = `${boardCode}${gradeCode}${subjCode}-${dateCode}-${randHex}`;

      const timestamp = Date.now();
      const ext = uploadedFile.name.split(".").pop();
      const base = uploadedFile.name.split(".")[0];
      const storageRef = ref(storage, `${uploadBoard}/${gradeObj?.label}/${base}_${timestamp}.${ext}`);
      await uploadBytes(storageRef, uploadedFile);
      const fileURL = await getDownloadURL(storageRef);

      await addDoc(collection(db, "resources"), {
        link: fileURL,
        referenceLink,
        title,
        type: uploadBoard,
        grade: gradeObj?.label || "",
        subject: subjectObj?.key || "",
        month: monthName,
        year: yearNum,
        uploadDate,
        refId,
      });

      showMsg("File uploaded successfully", "success");
    } catch (err) {
      showMsg("Error uploading file", "error");
      console.error(err);
    } finally {
      setUploadBoard(""); setUploadGradeId(""); setUploadSubjectId("");
      setUploadDate(todayStr()); setTitle(""); setFile(null); setReferenceLink("");
      setLoading(false);
      fetchPDFs();
    }
  };

  const handlePDFDelete = async (pdfId, pdfLink) => {
    setLoading(true);
    try {
      try { if (pdfLink) await deleteObject(ref(storage, pdfLink)); } catch (_) {}
      await deleteDoc(doc(db, "resources", pdfId));
      setPdfs((prev) => prev.filter((p) => p.id !== pdfId));
      showMsg("PDF deleted successfully", "success");
    } catch {
      showMsg("Error deleting PDF", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Ads ────────────────────────────────────────────────────────────────────
  const [adTitle, setAdTitle] = useState("");
  const [adImage, setAdImage] = useState(null);
  const [ads, setAds] = useState([]);

  const fetchAds = async () => {
    const snap = await getDocs(collection(db, "ads"));
    setAds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleAdUpload = async (e) => {
    e.preventDefault();
    if (!adImage || !adTitle) { showMsg("Please fill all ad fields.", "error"); return; }
    setLoading(true);
    try {
      const timestamp = Date.now();
      const ext = adImage.name.split(".").pop();
      const adRef = ref(storage, `ads/${adImage.name.split(".")[0]}_${timestamp}.${ext}`);
      await uploadBytes(adRef, adImage);
      const adImageURL = await getDownloadURL(adRef);
      await addDoc(collection(db, "ads"), { title: adTitle, imageURL: adImageURL });
      showMsg("Ad uploaded successfully", "success");
      setAdTitle(""); setAdImage(null);
      fetchAds();
    } catch {
      showMsg("Error uploading ad", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAdDelete = async (adId, adImageURL) => {
    setLoading(true);
    try {
      try { if (adImageURL) await deleteObject(ref(storage, adImageURL)); } catch (_) {}
      await deleteDoc(doc(db, "ads", adId));
      setAds((prev) => prev.filter((a) => a.id !== adId));
      showMsg("Ad deleted successfully", "success");
    } catch {
      showMsg("Error deleting ad", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── WhatsApp Group Leads ───────────────────────────────────────────────────
  const [whatsappLeads, setWhatsappLeads] = useState([]);

  const fetchWhatsappLeads = async () => {
    const snap = await getDocs(collection(db, "whatsappLeads"));
    setWhatsappLeads(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
    );
  };

  const handleWhatsappLeadDelete = async (id) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "whatsappLeads", id));
      setWhatsappLeads((prev) => prev.filter((l) => l.id !== id));
      showMsg("Lead deleted", "success");
    } catch {
      showMsg("Error deleting lead", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Student Leads ──────────────────────────────────────────────────────────
  const [studentLeads, setStudentLeads] = useState([]);

  const fetchLeads = async () => {
    const snap = await getDocs(collection(db, "studentLeads"));
    setStudentLeads(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        })
    );
  };

  const handleLeadDelete = async (leadId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "studentLeads", leadId));
      setStudentLeads((prev) => prev.filter((l) => l.id !== leadId));
      showMsg("Lead deleted", "success");
    } catch {
      showMsg("Error deleting lead", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Forms ──────────────────────────────────────────────────────────────────
  const [formTitle, setFormTitle] = useState("");
  const [formLink, setFormLink] = useState("");
  const [forms, setForms] = useState([]);

  const fetchForms = async () => {
    const snap = await getDocs(collection(db, "forms"));
    setForms(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    );
  };

  const handleFormUpload = async (e) => {
    e.preventDefault();
    if (!formTitle || !formLink) { showMsg("Please provide form title and link.", "error"); return; }
    setLoading(true);
    try {
      await addDoc(collection(db, "forms"), {
        title: formTitle, link: formLink, createdAt: new Date().toISOString(),
      });
      showMsg("Form added successfully", "success");
      setFormTitle(""); setFormLink("");
      fetchForms();
    } catch {
      showMsg("Error adding form", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormDelete = async (fId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "forms", fId));
      setForms((prev) => prev.filter((f) => f.id !== fId));
      showMsg("Form deleted successfully", "success");
    } catch {
      showMsg("Error deleting form", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── News ───────────────────────────────────────────────────────────────────
  const [newsTitle, setNewsTitle] = useState("");
  const [newsMessage, setNewsMessage] = useState("");
  const [newsLink, setNewsLink] = useState("");
  const [news, setNews] = useState([]);

  const fetchNews = async () => {
    const snap = await getDocs(collection(db, "news"));
    setNews(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    );
  };

  const handleNewsUpload = async (e) => {
    e.preventDefault();
    if (!newsTitle.trim() || !newsMessage.trim()) {
      showMsg("Please provide a news title and message.", "error");
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, "news"), {
        title: newsTitle.trim(),
        message: newsMessage.trim(),
        link: newsLink.trim() || null,
        visible: true,
        createdAt: new Date().toISOString(),
      });
      showMsg("News added successfully", "success");
      setNewsTitle(""); setNewsMessage(""); setNewsLink("");
      fetchNews();
    } catch {
      showMsg("Error adding news", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNewsVisible = async (n) => {
    const next = n.visible === false; // hidden -> show, visible -> hide
    try {
      await updateDoc(doc(db, "news", n.id), { visible: next });
      setNews((prev) => prev.map((x) => (x.id === n.id ? { ...x, visible: next } : x)));
    } catch {
      showMsg("Error updating news", "error");
    }
  };

  const handleNewsDelete = async (nId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "news", nId));
      setNews((prev) => prev.filter((n) => n.id !== nId));
      showMsg("News deleted successfully", "success");
    } catch {
      showMsg("Error deleting news", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── MCQ Tests ─────────────────────────────────────────────────────────────
  const [mcqBoard, setMcqBoard]         = useState("state");
  const [mcqExamType, setMcqExamType]   = useState("");
  const [mcqGrade, setMcqGrade]         = useState("");
  const [mcqSubject, setMcqSubject]     = useState("");
  const [mcqTopic, setMcqTopic]         = useState("");
  const [mcqHtml, setMcqHtml]           = useState("");
  const [mcqAnswerKey, setMcqAnswerKey] = useState("");
  const [mcqCount, setMcqCount]         = useState("");
  const [mcqDuration, setMcqDuration]   = useState("");
  const [mcqList, setMcqList]           = useState([]);

  const fetchMCQs = async () => {
    const snap = await getDocs(collection(db, "mcqs"));
    setMcqList(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0))
    );
  };

  const handleMCQUpload = async () => {
    if ((mcqBoard !== "competitive" && !mcqGrade) || !mcqTopic || !mcqHtml) {
      showMsg("Please fill Board, Topic and HTML content.", "error"); return;
    }
    setLoading(true);
    try {
      const randHex = Math.floor(Math.random() * 65536).toString(16).toUpperCase().padStart(4, "0");
      const dateCode = new Date().toISOString().slice(2, 10).replace(/-/g, "");
      const refId = `MCQ-${mcqBoard.slice(0, 3).toUpperCase()}${String(mcqGrade).padStart(2, "0")}-${dateCode}-${randHex}`;
      await addDoc(collection(db, "mcqs"), {
        board: mcqBoard,
        ...(mcqBoard === "competitive" && mcqExamType.trim() ? { examType: mcqExamType.trim() } : {}),
        grade: mcqGrade, topic: mcqTopic,
        ...(mcqSubject.trim() ? { subject: toKey(mcqSubject) } : {}),
        htmlContent: mcqHtml, answerKey: mcqAnswerKey,
        questionCount: Number(mcqCount) || 0,
        ...(Number(mcqDuration) > 0 ? { durationMins: Number(mcqDuration) } : {}),
        createdAt: new Date().toISOString(), refId,
      });
      showMsg("MCQ test uploaded successfully!", "success");
      setMcqGrade(""); setMcqSubject(""); setMcqTopic(""); setMcqHtml(""); setMcqAnswerKey(""); setMcqCount(""); setMcqDuration(""); setMcqExamType("");
      fetchMCQs();
    } catch {
      showMsg("Error uploading MCQ test.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMCQDelete = async (id) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "mcqs", id));
      setMcqList((prev) => prev.filter((m) => m.id !== id));
      showMsg("MCQ test deleted.", "success");
    } catch {
      showMsg("Error deleting MCQ test.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Students (gamified portal accounts) ─────────────────────────────────────
  const [stuName, setStuName]         = useState("");
  const [stuEmail, setStuEmail]       = useState("");
  const [stuPassword, setStuPassword] = useState("");
  const [stuBoard, setStuBoard]       = useState("");
  const [stuGradeId, setStuGradeId]   = useState("");
  const [stuGrades, setStuGrades]     = useState([]);
  const [students, setStudents]       = useState([]);

  // Cascade grades when board changes (reuses curriculum).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!stuBoard) { setStuGrades([]); setStuGradeId(""); return; }
    getDocs(gradesColl(stuBoard)).then((snap) =>
      setStuGrades(sortByOrder(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
    );
    setStuGradeId("");
  }, [stuBoard]); // eslint-disable-line

  const fetchStudents = async () => {
    const snap = await getDocs(collection(db, "students"));
    setStudents(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    );
  };

  const genPassword = () => {
    const pwd = Math.random().toString(36).slice(-8) + Math.floor(Math.random() * 90 + 10);
    setStuPassword(pwd);
  };

  const handleCreateStudent = async () => {
    if (!stuName.trim() || !stuEmail.trim() || !stuPassword.trim()) {
      showMsg("Please fill name, email and password.", "error"); return;
    }
    if (stuPassword.length < 6) { showMsg("Password must be at least 6 characters.", "error"); return; }
    setLoading(true);
    try {
      // 1) Create the auth account via secondary app (admin stays logged in).
      const uid = await createStudentAccount({ email: stuEmail.trim(), password: stuPassword });

      // 2) Allocate a stable anonymous handle number.
      const counterRef = doc(db, "counters", "students");
      const seq = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const next = (snap.exists() ? (snap.data().seq || 0) : 0) + 1;
        tx.set(counterRef, { seq: next }, { merge: true });
        return next;
      });

      const gradeObj = stuGrades.find((g) => g.id === stuGradeId);

      // 3) Write the student profile.
      await setDoc(doc(db, "students", uid), {
        role: "student",
        email: stuEmail.trim(),
        displayName: stuName.trim(),
        handle: `Student #${seq}`,
        board: stuBoard || "",
        grade: gradeObj?.label || "",
        active: true,
        xp: 0,
        level: 1,
        totalAttempts: 0,
        badges: [],
        streak: { current: 0, longest: 0, lastActiveDate: null },
        createdAt: new Date().toISOString(),
      });

      showMsg(`Student created (Student #${seq}).`, "success");
      setStuName(""); setStuEmail(""); setStuPassword(""); setStuBoard(""); setStuGradeId("");
      fetchStudents();
    } catch (err) {
      const code = err?.code || "";
      const msg = code === "auth/email-already-in-use" ? "That email is already registered."
        : code === "auth/invalid-email" ? "Invalid email address."
        : code === "auth/weak-password" ? "Password is too weak."
        : "Error creating student.";
      showMsg(msg, "error");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudentActive = async (stu) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, "students", stu.id), { active: !(stu.active !== false) });
      setStudents((prev) => prev.map((s) => s.id === stu.id ? { ...s, active: !(s.active !== false) } : s));
      showMsg(stu.active !== false ? "Student deactivated." : "Student reactivated.", "success");
    } catch {
      showMsg("Error updating student.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetOnboarding = async (stu) => {
    if (!window.confirm("Clear this student's board & class? They'll be asked again on next login.")) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "students", stu.id), { board: "", grade: "" });
      setStudents((prev) => prev.map((s) => s.id === stu.id ? { ...s, board: "", grade: "" } : s));
      showMsg("Onboarding reset.", "success");
    } catch {
      showMsg("Error resetting onboarding.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPDFs(); fetchAds(); fetchForms(); fetchNews(); fetchLeads(); fetchWhatsappLeads(); fetchMCQs(); fetchStudents(); }, []); // eslint-disable-line

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-400 p-4 sm:p-8 flex flex-col items-center">
      {showAlert && <Alert type={alertType} message={alertMessage} />}

      {/* Logout */}
      <div className="self-end mb-4 mr-0 sm:mr-4">
        <button
          onClick={handleLogout}
          className="relative px-5 py-3 overflow-hidden font-medium text-blue-600 bg-blue-100 rounded-full shadow-inner group"
        >
          <span className="absolute top-0 left-0 w-0 h-0 transition-all duration-200 border-t-2 border-blue-600 group-hover:w-full ease"></span>
          <span className="absolute bottom-0 right-0 w-0 h-0 transition-all duration-200 border-b-2 border-blue-600 group-hover:w-full ease"></span>
          <span className="absolute top-0 left-0 w-full h-0 transition-all duration-300 delay-200 bg-blue-600 group-hover:h-full ease"></span>
          <span className="absolute bottom-0 left-0 w-full h-0 transition-all duration-300 delay-200 bg-blue-600 group-hover:h-full ease"></span>
          <span className="absolute inset-0 w-full h-full duration-300 delay-300 bg-blue-900 opacity-0 group-hover:opacity-100"></span>
          <span className="relative transition-colors duration-300 delay-200 group-hover:text-white ease">Logout</span>
        </button>
      </div>

      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg w-full max-w-5xl">
        {loading ? (
          <Loader />
        ) : (
          <div className="grid grid-cols-1 gap-8">

            {/* ══ CURRICULUM MANAGER ══════════════════════════════════════════ */}
            <div className="p-6 border-2 border-indigo-100 rounded-2xl bg-gradient-to-br from-slate-50 to-indigo-50/40">
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800">📚 Curriculum Manager</h2>
                <p className="text-sm text-gray-500 mt-1">Build your academic structure: Board → Grade → Subject</p>
              </div>

              {/* Board Selector */}
              <div className="flex gap-1 mb-6 p-1 bg-white rounded-xl shadow-sm border border-gray-200 w-fit">
                {BOARDS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => { setCurrBoard(b.id); setEditItem(null); }}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      currBoard === b.id
                        ? "bg-indigo-600 text-white shadow-md"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>

              {/* Breadcrumb */}
              {selGrade && (
                <div className="flex items-center gap-1.5 mb-5 text-xs font-medium flex-wrap">
                  <span className="text-indigo-600 font-semibold">{BOARDS.find((b) => b.id === currBoard)?.name}</span>
                  <span className="text-gray-300 text-base">›</span>
                  <span className="bg-indigo-100 text-indigo-700 px-2.5 py-0.5 rounded-full">{selGrade.name}</span>
                </div>
              )}

              {/* Two Panels */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* ── Grades Panel ── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-gray-700">🎓 Grades / Classes</h3>
                    <span className="bg-indigo-100 text-indigo-600 text-xs px-2 py-0.5 rounded-full font-medium">{grades.length}</span>
                  </div>
                  <div className="p-3">
                    <div className="flex gap-2 mb-3">
                      <input
                        value={newGradeName}
                        onChange={(e) => setNewGradeName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddGrade()}
                        placeholder="e.g. Class 10"
                        className="flex-1 border border-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
                      />
                      <button
                        onClick={handleAddGrade}
                        className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors"
                      >+</button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {grades.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No grades added yet</p>}
                      {grades.map((g) => (
                        <PanelRow
                          key={g.id}
                          item={g}
                          isSelected={selGrade?.id === g.id}
                          isEditing={editItem?.level === "grade" && editItem?.id === g.id}
                          editName={editName}
                          onEditNameChange={setEditName}
                          onSelect={() => { setSelGrade(g); fetchSubjects(currBoard, g.id); setEditItem(null); }}
                          onEdit={() => { setEditItem({ level: "grade", id: g.id }); setEditName(g.name); }}
                          onSave={() => handleUpdateGrade(g.id)}
                          onCancel={() => setEditItem(null)}
                          onDelete={() => handleDeleteGrade(g.id)}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Subjects Panel ── */}
                <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${selGrade ? "border-gray-200" : "border-gray-100 opacity-50 pointer-events-none"}`}>
                  <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-700">📖 Subjects</h3>
                      {selGrade && <p className="text-xs text-emerald-600 mt-0.5">{selGrade.name}</p>}
                    </div>
                    <span className="bg-emerald-100 text-emerald-600 text-xs px-2 py-0.5 rounded-full font-medium">{subjects.length}</span>
                  </div>
                  <div className="p-3">
                    {selGrade ? (
                      <>
                        <div className="flex gap-2 mb-3">
                          <input
                            value={newSubjectName}
                            onChange={(e) => setNewSubjectName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddSubject()}
                            placeholder="e.g. Mathematics"
                            className="flex-1 border border-gray-200 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                          />
                          <button
                            onClick={handleAddSubject}
                            className="bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors"
                          >+</button>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {subjects.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No subjects added yet</p>}
                          {subjects.map((s) => (
                            <PanelRow
                              key={s.id}
                              item={s}
                              isSelected={false}
                              isEditing={editItem?.level === "subject" && editItem?.id === s.id}
                              editName={editName}
                              onEditNameChange={setEditName}
                              onSelect={() => {}}
                              onEdit={() => { setEditItem({ level: "subject", id: s.id }); setEditName(s.name); }}
                              onSave={() => handleUpdateSubject(s.id)}
                              onCancel={() => setEditItem(null)}
                              onDelete={() => handleDeleteSubject(s.id)}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-2xl mb-2">👆</p>
                        <p className="text-xs text-gray-400">Select a grade first</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* ══ PRICING ═════════════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-indigo-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">Pricing Section</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {Object.keys(cards).map((category, index) => (
                  <div key={index} className="flex flex-col gap-4">
                    <h3 className="text-lg text-blue-500 font-semibold bg-gray-200 px-3.5 py-1.5 rounded">
                      {categoryNames[category] || category}
                    </h3>
                    {cards[category].map((card, idx) => (
                      <input
                        key={idx}
                        type="text"
                        value={card[Object.keys(card)[1]]}
                        onChange={(e) => handleInputChange(category, idx, e)}
                        className="p-2 border border-gray-300 rounded"
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="flex justify-center mt-6">
                <button onClick={handleUpdate} className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600">
                  Update
                </button>
              </div>
            </div>

            {/* ══ UPLOAD PDF ══════════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-indigo-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">Upload PDF</h2>
              <div className="flex flex-col gap-4">

                {/* Row 1: Board, Grade, Date */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <select
                    value={uploadBoard}
                    onChange={(e) => setUploadBoard(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  >
                    <option value="">Select Board</option>
                    {BOARDS.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>

                  <select
                    value={uploadGradeId}
                    onChange={(e) => setUploadGradeId(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                    disabled={!uploadBoard || uploadGrades.length === 0}
                  >
                    <option value="">Select Grade</option>
                    {uploadGrades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500 font-medium">Paper Date</label>
                    <input
                      type="date"
                      value={uploadDate}
                      onChange={(e) => setUploadDate(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    />
                  </div>
                </div>

                {/* Row 2: Subject */}
                <select
                  value={uploadSubjectId}
                  onChange={(e) => setUploadSubjectId(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                  disabled={!uploadGradeId || uploadSubjects.length === 0}
                >
                  <option value="">Select Subject</option>
                  {uploadSubjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>

                <input
                  type="text"
                  placeholder="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />

                <input
                  type="text"
                  placeholder="Reference Link (optional)"
                  value={referenceLink}
                  onChange={(e) => setReferenceLink(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />

                <div className="flex flex-col items-center justify-center border-4 border-dashed border-gray-300 rounded-lg p-6 h-48">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="fileUpload"
                    disabled={isUploadDisabled}
                  />
                  <label
                    htmlFor="fileUpload"
                    className={`cursor-pointer py-2 px-6 rounded-lg transition-colors font-medium ${
                      isUploadDisabled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-400"
                    }`}
                  >
                    {file ? `Selected: ${file.name}` : "Choose a PDF File"}
                  </label>
                  <p className="mt-3 text-gray-400 text-sm text-center">
                    {isUploadDisabled ? "Fill all fields above to enable upload" : "Click to select a PDF file"}
                  </p>
                </div>
              </div>
            </div>

            {/* ══ UPLOADED PDFs ════════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-indigo-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">Uploaded PDFs</h2>

              {pdfs.length === 0 ? (
                <p className="text-gray-400 text-center">No PDFs uploaded yet.</p>
              ) : (() => {
                // Derive unique grade & subject lists from all uploaded PDFs
                const allGradeOpts = [...new Set(pdfs.map((p) => p.grade).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
                const allSubjectOpts = [...new Set(pdfs.map((p) => p.subject).filter(Boolean))].sort();

                const filteredPdfs = pdfs.filter((p) => {
                  if (pdfFilterGrade && p.grade !== pdfFilterGrade) return false;
                  if (pdfFilterSubject && p.subject !== pdfFilterSubject) return false;
                  return true;
                });

                const allFilteredIds = filteredPdfs.map((p) => p.id);
                const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedPdfs.has(id));

                const toggleSelectAll = () => {
                  if (allSelected) {
                    setSelectedPdfs((prev) => { const next = new Set(prev); allFilteredIds.forEach((id) => next.delete(id)); return next; });
                  } else {
                    setSelectedPdfs((prev) => new Set([...prev, ...allFilteredIds]));
                  }
                };

                const handleDeleteSelected = async () => {
                  if (!selectedPdfs.size) return;
                  if (!window.confirm(`Delete ${selectedPdfs.size} selected PDF(s)?`)) return;
                  const count = selectedPdfs.size;
                  setLoading(true);
                  try {
                    for (const id of selectedPdfs) {
                      const pdf = pdfs.find((p) => p.id === id);
                      if (!pdf) continue;
                      try { if (pdf.link) await deleteObject(ref(storage, pdf.link)); } catch (_) {}
                      await deleteDoc(doc(db, "resources", id));
                    }
                    setSelectedPdfs(new Set());
                    showMsg(`Deleted ${count} PDF(s)`, "success");
                    fetchPDFs();
                  } catch {
                    showMsg("Error during bulk delete", "error");
                  } finally {
                    setLoading(false);
                  }
                };

                return (
                  <>
                    {/* Filter Bar */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                      <select
                        value={pdfFilterGrade}
                        onChange={(e) => { setPdfFilterGrade(e.target.value); setSelectedPdfs(new Set()); }}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">All Grades</option>
                        {allGradeOpts.map((g) => <option key={g} value={g}>Grade {g}</option>)}
                      </select>
                      <select
                        value={pdfFilterSubject}
                        onChange={(e) => { setPdfFilterSubject(e.target.value); setSelectedPdfs(new Set()); }}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm capitalize"
                      >
                        <option value="">All Subjects</option>
                        {allSubjectOpts.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                      </select>
                      <span className="text-xs text-gray-400 ml-auto">{filteredPdfs.length} of {pdfs.length} PDFs</span>
                    </div>

                    {/* Toolbar: Select All + Delete Selected */}
                    <div className="flex items-center gap-3 mb-3 px-1">
                      <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 accent-indigo-600"
                        />
                        {allSelected ? "Deselect All" : "Select All"}
                      </label>
                      {selectedPdfs.size > 0 && (
                        <button
                          onClick={handleDeleteSelected}
                          className="ml-2 px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center gap-1.5"
                        >
                          🗑 Delete Selected ({selectedPdfs.size})
                        </button>
                      )}
                    </div>

                    {/* Scrollable Card Grid */}
                    <div className="overflow-y-auto max-h-[520px] pr-1">
                      {filteredPdfs.length === 0 ? (
                        <p className="text-gray-400 text-center py-8">No PDFs match the selected filters.</p>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {filteredPdfs.map((pdf) => (
                            <div
                              key={pdf.id}
                              className={`flex flex-col gap-2 border-2 p-4 rounded-xl transition-colors ${
                                selectedPdfs.has(pdf.id) ? "border-indigo-400 bg-indigo-50" : "border-gray-200 bg-white"
                              }`}
                            >
                              {/* Checkbox + Title row */}
                              <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedPdfs.has(pdf.id)}
                                  onChange={() => {
                                    setSelectedPdfs((prev) => {
                                      const next = new Set(prev);
                                      next.has(pdf.id) ? next.delete(pdf.id) : next.add(pdf.id);
                                      return next;
                                    });
                                  }}
                                  className="mt-0.5 w-4 h-4 accent-indigo-600 shrink-0"
                                />
                                <span className="font-medium text-sm leading-snug line-clamp-2">{pdf.title}</span>
                              </label>

                              {pdf.refId && (
                                <p className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-1 rounded w-fit">
                                  Ref: {pdf.refId}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 capitalize">
                                Board: {pdf.type} · Grade {pdf.grade} · {pdf.subject}
                                {pdf.uploadDate ? ` · ${pdf.uploadDate}` : pdf.month ? ` · ${pdf.month} ${pdf.year}` : ""}
                              </p>
                              {pdf.referenceLink && (
                                <a href={pdf.referenceLink} className="text-blue-500 text-xs hover:underline" target="_blank" rel="noopener noreferrer">
                                  📎 Reference Link
                                </a>
                              )}
                              <button
                                onClick={() => handlePDFDelete(pdf.id, pdf.link)}
                                className="px-2 py-1 mt-auto text-white bg-red-500 rounded-lg hover:bg-red-600 text-xs transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* ══ AD MANAGEMENT ═══════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-green-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">Ad Management</h2>
              <div className="flex flex-col gap-4 max-w-lg mx-auto">
                <input
                  type="text"
                  placeholder="Ad Title"
                  value={adTitle}
                  onChange={(e) => setAdTitle(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setAdImage(e.target.files[0])}
                  className="p-2 border border-gray-300 rounded"
                />
                <button
                  onClick={handleAdUpload}
                  className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600"
                  disabled={!adTitle || !adImage}
                >
                  Upload Ad
                </button>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {ads.map((ad) => (
                  <div key={ad.id} className="border p-4 rounded flex flex-col items-center">
                    <h4 className="font-semibold">{ad.title}</h4>
                    <img src={ad.imageURL} alt={ad.title} className="w-full h-auto mt-2 rounded" />
                    <button
                      onClick={() => handleAdDelete(ad.id, ad.imageURL)}
                      className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700 text-sm"
                    >
                      Delete Ad
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ══ NEWS MANAGEMENT ═════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-sky-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">News Management</h2>
              <p className="text-center text-sm text-gray-500 -mt-3 mb-4">
                Shown as a scrolling ticker on the homepage hero.
              </p>
              <form onSubmit={handleNewsUpload} className="flex flex-col gap-3 max-w-2xl mx-auto">
                <input
                  type="text"
                  placeholder="News Title"
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />
                <textarea
                  placeholder="News Message"
                  value={newsMessage}
                  onChange={(e) => setNewsMessage(e.target.value)}
                  rows={3}
                  className="p-2 border border-gray-300 rounded resize-y"
                />
                <input
                  type="url"
                  placeholder="Link (optional, https://...)"
                  value={newsLink}
                  onChange={(e) => setNewsLink(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="bg-sky-500 text-white px-4 py-2 rounded hover:bg-sky-600 disabled:opacity-50"
                    disabled={!newsTitle.trim() || !newsMessage.trim()}
                  >
                    Add News
                  </button>
                  <button
                    type="button"
                    onClick={() => { setNewsTitle(""); setNewsMessage(""); setNewsLink(""); }}
                    className="px-4 py-2 rounded border"
                  >
                    Clear
                  </button>
                </div>
              </form>
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold">Published News</h3>
                  <span className="text-xs text-gray-400">{news.length} item{news.length !== 1 ? "s" : ""}</span>
                </div>
                {news.length === 0 ? (
                  <p className="text-gray-500">No news added yet.</p>
                ) : (
                  <div className="overflow-y-auto max-h-72 pr-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {news.map((n) => (
                        <div key={n.id} className={`border p-3 rounded-xl flex items-start justify-between gap-3 transition-colors ${n.visible === false ? "border-gray-200 bg-gray-50 opacity-70" : "border-gray-200 bg-white hover:border-sky-300"}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-sm truncate">{n.title}</p>
                              {n.visible === false && (
                                <span className="text-[10px] font-bold uppercase text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded shrink-0">Hidden</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {formatDate(n.createdAt)}
                              {n.link ? (
                                <> · <a href={n.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">link</a></>
                              ) : ""}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <button
                              onClick={() => handleToggleNewsVisible(n)}
                              className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs transition-colors"
                            >
                              {n.visible === false ? "Show" : "Hide"}
                            </button>
                            <button
                              onClick={() => handleNewsDelete(n.id)}
                              className="px-2.5 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ══ FORM MANAGEMENT ═════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-yellow-500 rounded-xl">
              <h2 className="text-2xl font-bold mb-4 text-center">Form Management</h2>
              <form onSubmit={handleFormUpload} className="flex flex-col gap-3 max-w-2xl mx-auto">
                <input
                  type="text"
                  placeholder="Form Title"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />
                <input
                  type="url"
                  placeholder="Form Link (https://...)"
                  value={formLink}
                  onChange={(e) => setFormLink(e.target.value)}
                  className="p-2 border border-gray-300 rounded"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                    disabled={!formTitle || !formLink}
                  >
                    Add Form
                  </button>
                  <button
                    type="button"
                    onClick={() => { setFormTitle(""); setFormLink(""); }}
                    className="px-4 py-2 rounded border"
                  >
                    Clear
                  </button>
                </div>
              </form>
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold">Uploaded Forms</h3>
                  <span className="text-xs text-gray-400">{forms.length} form{forms.length !== 1 ? "s" : ""}</span>
                </div>
                {forms.length === 0 ? (
                  <p className="text-gray-500">No forms uploaded yet.</p>
                ) : (
                  <div className="overflow-y-auto max-h-72 pr-1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {forms.map((f) => (
                        <div key={f.id} className="border border-gray-200 bg-white p-3 rounded-xl flex items-center justify-between gap-3 hover:border-yellow-300 transition-colors">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{f.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{formatDate(f.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <a href={f.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-xs font-medium">Open</a>
                            <button
                              onClick={() => handleFormDelete(f.id)}
                              className="px-2.5 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ══ WHATSAPP GROUP LEADS ════════════════════════════════════ */}
            <div className="p-4 border-2 border-green-500 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">📲 WhatsApp Group Leads</h2>
                <span className="bg-green-100 text-green-700 text-sm font-bold px-3 py-1 rounded-full">
                  {whatsappLeads.length} leads
                </span>
              </div>
              {whatsappLeads.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No WhatsApp leads yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="bg-green-50 text-green-800 text-xs uppercase tracking-wide">
                        <th className="px-3 py-2 rounded-tl-lg">Date</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Who</th>
                        <th className="px-3 py-2">WhatsApp Number</th>
                        <th className="px-3 py-2 rounded-tr-lg"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {whatsappLeads.map((lead, i) => {
                        const date = lead.createdAt?.toDate
                          ? lead.createdAt.toDate().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                          : "—";
                        return (
                          <tr key={lead.id} className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-green-50/30"}`}>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{date}</td>
                            <td className="px-3 py-2 font-medium">{lead.name || "—"}</td>
                            <td className="px-3 py-2">
                              {lead.whoFilling ? (
                                <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                                  {lead.whoFilling}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2 font-mono">{lead.phone || "—"}</td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => handleWhatsappLeadDelete(lead.id)}
                                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ══ STUDENT LEADS ════════════════════════════════════════════ */}
            <div className="p-4 border-2 border-purple-500 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Student Leads</h2>
                <span className="bg-purple-100 text-purple-700 text-sm font-bold px-3 py-1 rounded-full">
                  {studentLeads.length} leads
                </span>
              </div>
              {studentLeads.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No student leads yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="bg-purple-50 text-purple-700 text-xs uppercase tracking-wide">
                        <th className="px-3 py-2 rounded-tl-lg">Date</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Who</th>
                        <th className="px-3 py-2">Board</th>
                        <th className="px-3 py-2">Class</th>
                        <th className="px-3 py-2">Subject</th>
                        <th className="px-3 py-2">Fav Subject</th>
                        <th className="px-3 py-2">Last %</th>
                        <th className="px-3 py-2">Best %</th>
                        <th className="px-3 py-2">Phone</th>
                        <th className="px-3 py-2">School</th>
                        <th className="px-3 py-2 rounded-tr-lg"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentLeads.map((lead, i) => {
                        const date = lead.createdAt?.toDate
                          ? lead.createdAt.toDate().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                          : "—";
                        return (
                          <tr key={lead.id} className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-purple-50/30"}`}>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{date}</td>
                            <td className="px-3 py-2 font-medium">{lead.name || "—"}</td>
                            <td className="px-3 py-2">
                              {lead.whoFilling ? (
                                <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                                  {lead.whoFilling}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2">{lead.board || "—"}</td>
                            <td className="px-3 py-2">{lead.class || "—"}</td>
                            <td className="px-3 py-2">{lead.subject || "—"}</td>
                            <td className="px-3 py-2">{lead.favSubject || "—"}</td>
                            <td className="px-3 py-2">{lead.lastScore != null ? `${lead.lastScore}%` : "—"}</td>
                            <td className="px-3 py-2">{lead.bestScore != null ? `${lead.bestScore}%` : "—"}</td>
                            <td className="px-3 py-2">{lead.phone || "—"}</td>
                            <td className="px-3 py-2">{lead.school || "—"}</td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => handleLeadDelete(lead.id)}
                                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-700"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── MCQ Tests Panel ──────────────────────────────────────────── */}
            <div className="border-2 border-orange-400 rounded-xl p-6">
              <h2 className="text-xl font-bold text-orange-700 mb-1">
                MCQ Tests
                <span className="ml-2 bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {mcqList.length} tests
                </span>
              </h2>
              <p className="text-gray-400 text-xs mb-5">Paste your MCQ HTML and provide the answer key to publish a test.</p>

              {/* Upload form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Board */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Board</label>
                  <select value={mcqBoard} onChange={(e) => { setMcqBoard(e.target.value); setMcqExamType(""); }}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
                    <option value="state">State Board</option>
                    <option value="cbse">CBSE</option>
                    <option value="competitive">Competitive</option>
                  </select>
                </div>
                {/* Exam type — shown only for Competitive */}
                {mcqBoard === "competitive" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Exam Type <span className="text-gray-400 font-normal">(e.g. NEET, JEE, TNPSC)</span>
                    </label>
                    <input type="text" placeholder="e.g. NEET, JEE Main, JEE Advanced"
                      value={mcqExamType} onChange={(e) => setMcqExamType(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                  </div>
                )}
                {/* Grade */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Class / Grade{mcqBoard === "competitive" && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
                  </label>
                  <input type="number" min="1" max="12" placeholder="e.g. 10"
                    value={mcqGrade} onChange={(e) => setMcqGrade(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* Topic */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Topic / Title</label>
                  <input type="text" placeholder="e.g. Algebra – Chapter 1"
                    value={mcqTopic} onChange={(e) => setMcqTopic(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* Subject (optional — powers weak-topic study recommendations) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Subject <span className="text-gray-400 font-normal">(optional — links weak topics to study materials)</span>
                  </label>
                  <input type="text" placeholder="e.g. Mathematics"
                    value={mcqSubject} onChange={(e) => setMcqSubject(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* Question count */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Number of Questions</label>
                  <input type="number" min="1" placeholder="e.g. 20"
                    value={mcqCount} onChange={(e) => setMcqCount(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* Exam Duration */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Exam Duration{" "}
                    <span className="text-gray-400 font-normal">(optional — minutes; blank = auto)</span>
                  </label>
                  <input type="number" min="1" placeholder="e.g. 30"
                    value={mcqDuration} onChange={(e) => setMcqDuration(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* Answer Key */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Answer Key{" "}
                    <span className="text-gray-400 font-normal">
                      (optional — comma-separated e.g. A,B,C,D… Leave blank if answers are embedded in the HTML)
                    </span>
                  </label>
                  <input type="text" placeholder="A,B,C,D,A,B,C,D... (leave blank if answers are in the HTML)"
                    value={mcqAnswerKey} onChange={(e) => setMcqAnswerKey(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300" />
                </div>
                {/* HTML content */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    HTML Content <span className="text-gray-400 font-normal">(paste your MCQ HTML here)</span>
                  </label>
                  <textarea rows={10} placeholder="Paste your MCQ HTML here..."
                    value={mcqHtml} onChange={(e) => setMcqHtml(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-300 resize-y" />
                </div>
              </div>

              <button
                onClick={handleMCQUpload}
                disabled={(mcqBoard !== "competitive" && !mcqGrade) || !mcqTopic || !mcqHtml}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors mb-6"
              >
                Upload MCQ Test
              </button>

              {/* MCQ list */}
              {mcqList.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">No MCQ tests uploaded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-gray-700 border-collapse">
                    <thead>
                      <tr className="bg-orange-50 text-orange-700 text-left">
                        <th className="px-3 py-2 font-semibold">Date</th>
                        <th className="px-3 py-2 font-semibold">Ref ID</th>
                        <th className="px-3 py-2 font-semibold">Board</th>
                        <th className="px-3 py-2 font-semibold">Class</th>
                        <th className="px-3 py-2 font-semibold">Topic</th>
                        <th className="px-3 py-2 font-semibold">Qs</th>
                        <th className="px-3 py-2 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mcqList.map((m, i) => (
                        <tr key={m.id} className={i % 2 === 0 ? "bg-white" : "bg-orange-50/30"}>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(m.createdAt)}</td>
                          <td className="px-3 py-2 font-mono text-orange-600 whitespace-nowrap">{m.refId || "—"}</td>
                          <td className="px-3 py-2 capitalize">{m.board}</td>
                          <td className="px-3 py-2">{m.grade}</td>
                          <td className="px-3 py-2 max-w-[180px] truncate">{m.topic}</td>
                          <td className="px-3 py-2">{m.questionCount || "—"}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => handleMCQDelete(m.id)}
                              className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-700">
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ══ STUDENT ACCOUNTS (Gamified Portal) ══════════════════════════ */}
            <div className="border-2 border-indigo-500 rounded-xl p-6">
              <h2 className="text-xl font-bold text-indigo-700 mb-1">
                👩‍🎓 Student Accounts
                <span className="ml-2 bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {students.length} student{students.length !== 1 ? "s" : ""}
                </span>
              </h2>
              <p className="text-gray-400 text-xs mb-5">Create login credentials for students to access the gamified learning dashboard.</p>

              {/* Create form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Student Name</label>
                  <input type="text" placeholder="e.g. Priya R" value={stuName}
                    onChange={(e) => setStuName(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                  <input type="email" placeholder="student@example.com" value={stuEmail}
                    onChange={(e) => setStuEmail(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                  <div className="flex gap-2">
                    <input type="text" placeholder="min 6 characters" value={stuPassword}
                      onChange={(e) => setStuPassword(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    <button type="button" onClick={genPassword}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200 whitespace-nowrap">
                      Generate
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Board <span className="text-gray-400 font-normal">(optional)</span></label>
                    <select value={stuBoard} onChange={(e) => setStuBoard(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                      <option value="">—</option>
                      {BOARDS.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Class <span className="text-gray-400 font-normal">(optional)</span></label>
                    <select value={stuGradeId} onChange={(e) => setStuGradeId(e.target.value)}
                      disabled={!stuBoard || stuGrades.length === 0}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:bg-gray-50">
                      <option value="">—</option>
                      {stuGrades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <button onClick={handleCreateStudent}
                disabled={!stuName.trim() || !stuEmail.trim() || !stuPassword.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors mb-6">
                Create Student
              </button>

              {/* Student list */}
              {students.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">No students yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-gray-700 border-collapse">
                    <thead>
                      <tr className="bg-indigo-50 text-indigo-700 text-left">
                        <th className="px-3 py-2 font-semibold">Handle</th>
                        <th className="px-3 py-2 font-semibold">Name</th>
                        <th className="px-3 py-2 font-semibold">Email</th>
                        <th className="px-3 py-2 font-semibold">Board</th>
                        <th className="px-3 py-2 font-semibold">Class</th>
                        <th className="px-3 py-2 font-semibold">Lvl</th>
                        <th className="px-3 py-2 font-semibold">XP</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s, i) => (
                        <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-indigo-50/30"}>
                          <td className="px-3 py-2 font-mono text-indigo-600 whitespace-nowrap">{s.handle || "—"}</td>
                          <td className="px-3 py-2 font-medium whitespace-nowrap">{s.displayName || "—"}</td>
                          <td className="px-3 py-2">{s.email || "—"}</td>
                          <td className="px-3 py-2 capitalize">{s.board || "—"}</td>
                          <td className="px-3 py-2">{s.grade || "—"}</td>
                          <td className="px-3 py-2">{s.level || 1}</td>
                          <td className="px-3 py-2">{s.xp || 0}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.active !== false ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}>
                              {s.active !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1.5 whitespace-nowrap">
                              <button onClick={() => handleToggleStudentActive(s)}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-[11px] hover:bg-gray-200">
                                {s.active !== false ? "Deactivate" : "Activate"}
                              </button>
                              <button onClick={() => handleResetOnboarding(s)}
                                className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-[11px] hover:bg-amber-200">
                                Reset
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
